'use client';
import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn, type AlertSeverity } from '../utils';
import { Dialog, DialogPanel } from '@headlessui/react';
import { Icon } from './icons/Icon';
import { AnimatePresence, motion } from 'framer-motion';
import type { IconName } from '../icons/names';
import { AlertBanner, type AlertBannerProps } from '../modules/validation/AlertBanner';

interface LayoutProp {
  layout?: 'row' | 'vertical';
}

// `max-w-full`: a sized panel never grows past the viewport padding on a narrow screen.
const ModalVariants = cva(`w-full max-w-full`, {
  variants: {
    size: {
      xs: 'sm:w-[400px]',
      sm: 'sm:w-[600px]',
      md: 'sm:w-[700px]',
      lg: 'sm:w-[800px]',
      xl: 'sm:w-[900px]',
      '2xl': 'sm:w-[1000px]',
      half: 'sm:w-[50vw]',
      full: 'w-full'
    }
  }
});

type ModalVariantProps = VariantProps<typeof ModalVariants>;

export interface ModalProps extends ModalVariantProps, LayoutProp {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  testId?: string;
}

const ModalBase = React.forwardRef<HTMLElement, ModalProps>(
  ({ children, open, onClose, size, className, layout = 'vertical', testId }, ref) => {
    return (
      <AnimatePresence>
        {open && (
          <Dialog
            ref={ref}
            open={open}
            onClose={onClose}
            as='div'
            className='relative z-[100] w-full'
          >
            <motion.div
              key='backdrop'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className='fixed inset-0 bg-overlays-50 p-md'
            />

            <div className='fixed inset-0 flex w-screen items-center justify-center p-md overflow-y-auto'>
              <motion.div
                key='modal'
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                className='w-full flex justify-center'
              >
                <DialogPanel
                  data-testid={testId}
                  className={cn(
                    'rounded-xl bg-background-input overflow-hidden shadow-xl max-h-[calc(100svh-2rem)] flex flex-col',
                    ModalVariants({ size }),
                    className,
                    layout === 'row' ? 'flex-row flex-wrap' : 'flex-col'
                  )}
                >
                  {React.Children.map(children, child => {
                    if (!React.isValidElement(child)) return child;
                    return React.cloneElement(child as React.ReactElement<LayoutProp>, {
                      layout
                    });
                  })}
                </DialogPanel>
              </motion.div>
            </div>
          </Dialog>
        )}
      </AnimatePresence>
    );
  }
);

ModalBase.displayName = 'ModalBase';

const ModalHeaderVariants = cva(``, {
  variants: {
    variant: {
      white: 'bg-background-input',
      grey: 'bg-background-input'
    }
  },
  defaultVariants: {
    variant: 'grey'
  }
});

type ModalHeaderVariantProps = VariantProps<typeof ModalHeaderVariants>;

export interface ModalHeader extends LayoutProp, ModalHeaderVariantProps {
  className?: string;
  onClose: () => void;
  children?: React.ReactNode;
  label?: string;
  /** Description text or rich content rendered below the label */
  description?: React.ReactNode;
  /** Optional icon element rendered to the left of the label/description */
  icon?: React.ReactNode;
  testId?: string;
}

const Header: React.FC<ModalHeader> = ({
  className,
  onClose,
  children,
  label,
  description,
  icon,
  variant,
  testId
}) => {
  // If children are provided without label, use them directly (custom header content)
  if (children && !label) {
    return (
      <header
        data-testid={testId}
        className={cn(
          className,
          'w-full flex flex-row flex-nowrap items-center justify-between px-lg py-md',
          ModalHeaderVariants({ variant })
        )}
      >
        <div className='flex items-center flex-1'>{children}</div>
        <div className='ml-md'>
          <Icon
            name='xmark'
            onClick={onClose}
            aria-label='Close'
            role='button'
            className='cursor-pointer text-xl text-foreground-muted transition hover:text-foreground-base'
          />
        </div>
      </header>
    );
  }

  // Default pattern with optional icon, label and description
  return (
    <header
      data-testid={testId}
      className={cn(
        className,
        'w-full flex flex-row flex-nowrap items-start justify-between',
        ModalHeaderVariants({ variant })
      )}
    >
      <div className='p-lg flex flex-row items-center gap-md flex-1 min-w-0'>
        {icon && (
          <div className='shrink-0 w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center'>
            {icon}
          </div>
        )}
        <div className='flex flex-col items-start min-w-0 flex-1 gap-sm'>
          <h2 className='text-base sm:text-xl font-medium text-foreground-base leading-snug'>
            {label}
          </h2>
          {description &&
            (typeof description === 'string' ? (
              <p className='text-xs sm:text-sm font-normal text-foreground-muted'>{description}</p>
            ) : (
              <div className='w-full text-xs sm:text-sm font-normal text-foreground-muted'>
                {description}
              </div>
            ))}
          {children}
        </div>
      </div>
      <div className='p-lg shrink-0'>
        <Icon
          name='xmark'
          onClick={onClose}
          aria-label='Close'
          role='button'
          className='cursor-pointer text-xl text-foreground-muted transition hover:text-foreground-base'
        />
      </div>
    </header>
  );
};

const ModalContentVariants = cva(``, {
  variants: {
    size: {
      sm: 'h-[400px]',
      md: 'h-[100%]',
      lg: 'h-[600px]',
      xl: 'h-[800px]',
      half: 'h-[50svh]',
      full: ''
    }
  },
  defaultVariants: {
    size: 'md'
  }
});

type ModalContentVariantProps = VariantProps<typeof ModalContentVariants>;

export interface ModalContent extends ModalContentVariantProps {
  className?: string;
  children?: React.ReactNode;
  testId?: string;
}

const Content: React.FC<ModalContent> = ({ className, children, size, testId }) => {
  return (
    <div
      data-testid={testId}
      className={cn(
        className,
        'overflow-y-auto p-md pt-lg pb-lg flex flex-col gap-sm flex-1 min-h-0',
        ModalContentVariants({ size })
      )}
    >
      {children}
    </div>
  );
};

const ModalFooterVariants = cva(``, {
  variants: {
    variant: {
      white: 'bg-background-input',
      grey: 'bg-background-input'
    }
  },
  defaultVariants: {
    variant: 'grey'
  }
});

type ModalFooterVariantProps = VariantProps<typeof ModalFooterVariants>;

export interface ModalFooter extends LayoutProp, ModalFooterVariantProps {
  className?: string;
  children?: React.ReactNode;
  testId?: string;
}

const Footer: React.FC<ModalFooter> = ({
  className,
  children,
  layout = 'vertical',
  variant,
  testId
}) => {
  return (
    <footer
      data-testid={testId}
      className={cn(
        className,
        'flex flex-row flex-nowrap items-center justify-end gap-md p-lg',
        layout === 'row' && 'border-s border-s-border-base w-[15%] flex-col justify-start',
        ModalFooterVariants({ variant })
      )}
    >
      {children}
    </footer>
  );
};

export interface ModalAction extends LayoutProp {
  className?: string;
  children?: React.ReactNode;
  testId?: string;
}

const Action: React.FC<ModalAction> = ({ className, children, layout = 'vertical', testId }) => {
  return (
    <div
      role='toolbar'
      data-testid={testId}
      className={cn('flex gap-md', layout === 'row' ? 'flex-col' : 'flex-row', className)}
    >
      {children}
    </div>
  );
};

export interface ModalBanner {
  /** Optional title line above the body */
  title?: React.ReactNode;
  /** Banner body */
  children?: React.ReactNode;
  /** Overrides the variant's default icon */
  icon?: IconName;
  /** Live-region role; none by default. Set it only for a message that appears after an action */
  role?: AlertBannerProps['role'];
  className?: string;
  testId?: string;
  id?: string;
}

/** Modal banners point at a problem with the same triangle for both error and warning. */
const BANNER_ICONS: Record<AlertSeverity, IconName> = {
  error: 'triangle-exclamation',
  warning: 'triangle-exclamation',
  info: 'circle-info',
  success: 'circle-check'
};

const createBanner = (variant: AlertSeverity, displayName: string) => {
  const Banner = React.forwardRef<HTMLDivElement, ModalBanner>(({ icon, ...props }, ref) => (
    <AlertBanner
      ref={ref}
      variant={variant}
      size='sm'
      icon={icon ?? BANNER_ICONS[variant]}
      {...props}
    />
  ));
  Banner.displayName = displayName;
  return Banner;
};

/** Warning banner for use inside a modal. */
const Warning = createBanner('warning', 'ModalWarning');
/** Error banner for use inside a modal. */
const ErrorBanner = createBanner('error', 'ModalError');
/** Informational banner for use inside a modal. */
const Info = createBanner('info', 'ModalInfo');
/** Success banner for use inside a modal. */
const Success = createBanner('success', 'ModalSuccess');

const Modal = Object.assign(ModalBase, {
  Header: Header,
  Content: Content,
  Footer: Footer,
  Action: Action,
  Warning: Warning,
  Error: ErrorBanner,
  Info: Info,
  Success: Success
});

export { Modal };

Modal.displayName = 'Modal';
Modal.Header.displayName = 'ModalHeader';
Modal.Content.displayName = 'ModalContent';
Modal.Footer.displayName = 'ModalFooter';
Modal.Action.displayName = 'ModalAction';
